import "../../party/partyShell.css";

/**
 * Public guest entry for private imposter online rooms (no account).
 * Opened from /party/join/imposter?code=ROOMCODE — same pattern as Answer Bluff join.
 */
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { alpha, useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";

import {
  effectiveImposterOnlineRole,
  extractImposterOnlineSecretWord,
  extractImposterOnlineSecretWordHints,
  shouldPullImposterMeDuringReveal,
} from "../../party/effectiveImposterOnlineRole";
import { imposterOnlineAvatarEmoji } from "../../party/imposterOnlineAvatar";
import { imposterOnlinePlayerLabel } from "../../party/imposterOnlinePlayerLabel";
import { ImposterOnlineCategoryBanner } from "../../party/ImposterOnlineCategoryBanner";
import { ImposterOnlineCrewHintsRow } from "../../party/ImposterOnlineCrewHintsRow";
import { ImposterOnlinePhaseHint } from "../../party/ImposterOnlinePhaseHint";
import { ImposterOnlinePlayersRoster } from "../../party/ImposterOnlinePlayersRoster";
import { ImposterOnlineQrSharePanel } from "../../party/ImposterOnlineQrSharePanel";
import { buildImposterOnlineJoinUrl, imposterOnlineWsUrl } from "../../party/imposterOnlineWsUrl";
import { imposterOnlineSessionRoundTotal } from "../../party/imposterOnlineSessionRoundTotal";
import { normalizeImposterOnlineMePatch } from "../../party/normalizeImposterOnlineMePatch";
import { PartyTopBar } from "../../party/PartyChrome";
import { partyOutlinedFieldSx } from "../../party/partyFieldSx";
import { PartyConfettiCards, PartyMascot } from "../../party/PartyMascot";
import { handlePartyRosterNameEnter } from "../../party/partyNameInputEnter";
import { useBluffPhaseCountdown } from "../../party/useBluffPhaseCountdown";
import type { PartyShellPalette } from "../../theme/theme";
import { apiClient } from "../../utils/api";

type GameRow = {
  display_name?: string;
  connected?: boolean;
  is_host?: boolean;
  description?: string;
  has_voted?: boolean;
};

type ImposterOnlineGame = {
  type?: string;
  phase?: string;
  phase_ends_at?: string | null;
  vote_started_at?: string | null;
  round_index?: number;
  session_round_total?: number;
  word_round_total?: number;
  last_completed_round_word?: string | null;
  players?: Record<string, GameRow>;
  player_order?: string[];
  category_name_en?: string;
  category_name_ar?: string;
  imposters?: string[];
  results?: {
    crew_wins?: boolean;
    secret_word?: string;
    secret_word_hints?: string[];
    session_words?: string[];
    imposters?: string[];
    credit_settled?: boolean;
  };
};

function normalizeRoomCode(raw: string | null): string {
  return (raw || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
}

function imposterWsPathFromRoomCode(code: string): string {
  const c = normalizeRoomCode(code);
  return c ? `/ws/imposter/${c}/` : "";
}

export default function PartyJoinImposterOnlinePage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const partyPal = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const codeFromUrl = normalizeRoomCode(searchParams.get("code"));

  const [guestName, setGuestName] = useState("");
  const [joined, setJoined] = useState(false);
  const [game, setGame] = useState<ImposterOnlineGame | null>(null);
  const [me, setMe] = useState<{
    secret_word?: string;
    secret_word_hints?: string[];
    is_imposter?: boolean;
    category_name_en?: string;
    category_name_ar?: string;
  } | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [describeDraft, setDescribeDraft] = useState("");
  const [draftFeed, setDraftFeed] = useState<{ from: string; text: string }[]>([]);
  const [votePick, setVotePick] = useState<string[]>([]);
  const [wsError, setWsError] = useState<string | null>(null);
  const [describeSubmitted, setDescribeSubmitted] = useState(false);
  const [voteSubmitted, setVoteSubmitted] = useState(false);
  const [joinLinkCopied, setJoinLinkCopied] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const imposterJoinDepsRef = useRef({ t });
  imposterJoinDepsRef.current = { t };
  const prevOnlineRoundRef = useRef<number | null>(null);
  const joinSentRef = useRef(false);
  const playerIdRef = useRef<string | null>(null);
  const lastDraftSentRef = useRef(0);
  const isImposterRef = useRef(false);
  const voteAutoSendRef = useRef(false);

  useEffect(() => {
    if (game?.phase !== "vote") voteAutoSendRef.current = false;
  }, [game?.phase]);

  const roomCode = codeFromUrl;
  const wsPath = imposterWsPathFromRoomCode(roomCode);

  useEffect(() => {
    playerIdRef.current = playerId;
  }, [playerId]);

  useEffect(() => {
    const ph = game?.phase ?? "lobby";
    isImposterRef.current = effectiveImposterOnlineRole(me, ph, playerId, game?.imposters) === true;
  }, [me, game?.phase, game?.imposters, playerId]);

  const fieldSx = useMemo(() => partyOutlinedFieldSx(partyPal), [partyPal]);
  const languageStartsWithAr = Boolean(i18n.language?.startsWith("ar"));

  const joinUrlGuest = useMemo(() => (roomCode ? buildImposterOnlineJoinUrl(roomCode) : ""), [roomCode]);

  const copyGuestJoinLink = useCallback(async () => {
    if (!joinUrlGuest) return;
    try {
      await navigator.clipboard.writeText(joinUrlGuest);
      setJoinLinkCopied(true);
      window.setTimeout(() => setJoinLinkCopied(false), 2200);
    } catch {
      /* ignore */
    }
  }, [joinUrlGuest]);

  const phaseSecondsLeft = useBluffPhaseCountdown(game?.phase_ends_at ?? null);
  const imposterCount = useMemo(() => {
    const n = game?.player_order?.length ?? 0;
    if (n < 3) return 1;
    return Math.max(1, Math.min(Math.floor((n - 1) / 3), n - 2));
  }, [game?.player_order]);

  useEffect(() => {
    if (game?.phase !== "vote") return;
    const locked = voteSubmitted || Boolean(playerId && game?.players?.[playerId]?.has_voted);
    if (locked) return;
    if (votePick.length !== imposterCount) return;
    if (voteAutoSendRef.current) return;
    voteAutoSendRef.current = true;
    setVoteSubmitted(true);
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setVoteSubmitted(false);
      voteAutoSendRef.current = false;
      return;
    }
    ws.send(JSON.stringify({ type: "imposter_vote", targets: votePick }));
  }, [game?.phase, voteSubmitted, votePick, imposterCount, playerId, game?.players]);

  const missingCode = !roomCode || roomCode.length < 4;

  /** WebSocket with backoff reconnect + foreground reconnect after background suspend (same as ``PartyPlayImposterOnline``). */
  useEffect(() => {
    if (!joined || !guestName.trim() || missingCode || !wsPath || !roomCode) return;

    let intentionalClose = false;
    let reconnectTimer: number | null = null;
    let reconnectAttempt = 0;

    const clearReconnectTimer = () => {
      if (reconnectTimer != null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const connectWs = () => {
      if (intentionalClose) return;
      const token = typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null;
      try {
        const prev = wsRef.current;
        if (prev) {
          try {
            prev.onclose = null;
            prev.close();
          } catch {
            /* ignore */
          }
          wsRef.current = null;
        }
        joinSentRef.current = false;
        const ws = new WebSocket(imposterOnlineWsUrl(wsPath, token));
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(String(ev.data)) as Record<string, unknown>;
            if (data.type === "system" && data.event === "seat_ready") {
              const rawPid = data.player_id;
              const pid =
                rawPid != null && String(rawPid).trim() !== "" ? String(rawPid).trim() : null;
              if (pid) setPlayerId(pid);
              if (data.me && typeof data.me === "object" && data.me !== null) {
                setMe((prev) =>
                  ({
                    ...(prev ?? {}),
                    ...normalizeImposterOnlineMePatch(data.me as Record<string, unknown>),
                  }) as typeof me,
                );
              }
              if (data.game && typeof data.game === "object") setGame(data.game as ImposterOnlineGame);
              return;
            }
            if (data.type === "me_refresh") {
              if (data.me && typeof data.me === "object" && data.me !== null) {
                setMe((prev) =>
                  ({
                    ...(prev ?? {}),
                    ...normalizeImposterOnlineMePatch(data.me as Record<string, unknown>),
                  }) as typeof me,
                );
              }
              return;
            }
            if (data.type === "imposter_online_game") {
              setGame(data as ImposterOnlineGame);
              return;
            }
            if (data.type === "imposter_draft") {
              const from = typeof data.from_player_id === "string" ? data.from_player_id : "";
              const text = typeof data.text === "string" ? data.text : "";
              if (isImposterRef.current && from && from !== playerIdRef.current) {
                setDraftFeed((prev) => [{ from, text }, ...prev].slice(0, 24));
              }
              return;
            }
            if (data.type === "ack") {
              const ev = typeof data.event === "string" ? data.event : "";
              const ok = Boolean(data.ok);
              if (ev === "description_rejected" || (ev === "description_saved" && !ok)) setDescribeSubmitted(false);
              if (ev === "vote_rejected" || (ev === "vote_saved" && !ok)) {
                setVoteSubmitted(false);
                voteAutoSendRef.current = false;
                setVotePick([]);
              }
              return;
            }
          } catch {
            /* ignore */
          }
        };
        ws.onopen = () => {
          reconnectAttempt = 0;
          clearReconnectTimer();
          setWsError(null);
        };
        ws.onerror = () => setWsError(imposterJoinDepsRef.current.t("party.playImposterOnline.wsError"));
        ws.onclose = () => {
          wsRef.current = null;
          if (intentionalClose) return;
          clearReconnectTimer();
          const delay = Math.min(30_000, 700 * Math.pow(2, Math.min(reconnectAttempt, 6)));
          reconnectAttempt += 1;
          reconnectTimer = window.setTimeout(() => {
            reconnectTimer = null;
            connectWs();
          }, delay);
        };
      } catch {
        setWsError(imposterJoinDepsRef.current.t("party.playImposterOnline.wsOpenFailed"));
        if (!intentionalClose) {
          clearReconnectTimer();
          const delay = Math.min(30_000, 700 * Math.pow(2, Math.min(reconnectAttempt, 6)));
          reconnectAttempt += 1;
          reconnectTimer = window.setTimeout(() => {
            reconnectTimer = null;
            connectWs();
          }, delay);
        }
      }
    };

    connectWs();

    const onVisibility = () => {
      if (intentionalClose || document.visibilityState !== "visible") return;
      const w = wsRef.current;
      if (!w || w.readyState !== WebSocket.OPEN) {
        clearReconnectTimer();
        reconnectAttempt = 0;
        connectWs();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      intentionalClose = true;
      clearReconnectTimer();
      document.removeEventListener("visibilitychange", onVisibility);
      joinSentRef.current = false;
      const w = wsRef.current;
      wsRef.current = null;
      try {
        if (w) {
          w.onclose = null;
          w.close();
        }
      } catch {
        /* ignore */
      }
    };
  }, [joined, guestName, missingCode, roomCode, wsPath]);

  useEffect(() => {
    if (game?.phase !== "vote") setVoteSubmitted(false);
  }, [game?.phase]);

  const sendJoin = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || joinSentRef.current) return;
    const name = guestName.trim();
    if (!name) return;
    ws.send(JSON.stringify({ type: "join", display_name: name }));
    joinSentRef.current = true;
  }, [guestName]);

  /** Match host: ping often while ``phase_ends_at`` is set so server deadline ticks run. */
  useEffect(() => {
    const ms = game?.phase_ends_at ? 2000 : 22_000;
    const id = window.setInterval(() => {
      const ws = wsRef.current;
      if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
    }, ms);
    return () => window.clearInterval(id);
  }, [game?.phase, game?.phase_ends_at]);

  useEffect(() => {
    if (!joined || !roomCode) return;
    const tick = window.setInterval(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN || joinSentRef.current) return;
      sendJoin();
    }, 400);
    return () => window.clearInterval(tick);
  }, [guestName, joined, roomCode, sendJoin]);

  useEffect(() => {
    if (!shouldPullImposterMeDuringReveal(me, game?.phase, playerId, game?.imposters)) return;
    const tick = () => {
      const w = wsRef.current;
      if (w?.readyState === WebSocket.OPEN) w.send(JSON.stringify({ type: "pull_me" }));
    };
    tick();
    const iv = window.setInterval(tick, 800);
    return () => window.clearInterval(iv);
  }, [game?.phase, game?.imposters, playerId, me]);

  useEffect(() => {
    if (!joined || !roomCode || !playerId) return;
    if (!shouldPullImposterMeDuringReveal(me, game?.phase, playerId, game?.imposters)) return;

    const run = () => {
      const body: { player_id: string; display_name?: string } = { player_id: playerId };
      if (playerId.startsWith("nm_")) {
        const nm = guestName.trim();
        if (!nm) return;
        body.display_name = nm;
      }
      void apiClient
        .postImposterOnlineRoomHints(roomCode, body)
        .then((res) => {
          const patch = res?.me;
          if (patch && typeof patch === "object") {
            setMe((prev) =>
              ({
                ...(prev ?? {}),
                ...normalizeImposterOnlineMePatch(patch as Record<string, unknown>),
              }) as typeof me,
            );
          }
        })
        .catch(() => { });
    };
    run();
    const iv = window.setInterval(run, 1200);
    return () => window.clearInterval(iv);
  }, [guestName, joined, roomCode, playerId, game?.phase, game?.imposters, me]);

  useEffect(() => {
    if (!game?.phase || game.phase !== "describe") return;
    if (effectiveImposterOnlineRole(me, game.phase, playerIdRef.current, game?.imposters) !== true) return;
    const pid = playerIdRef.current;
    const desc = pid ? (game.players ?? {})[pid]?.description : undefined;
    if (desc?.trim()) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - lastDraftSentRef.current < 300) return;
    lastDraftSentRef.current = now;
    ws.send(JSON.stringify({ type: "imposter_draft", text: describeDraft }));
  }, [describeDraft, game?.phase, game?.players, game?.imposters, me]);

  useEffect(() => {
    if (game?.phase !== "describe") setDescribeSubmitted(false);
  }, [game?.phase]);

  useEffect(() => {
    const ph = game?.phase;
    if (ph === "between_rounds") {
      setDescribeDraft("");
      setDraftFeed([]);
      setVotePick([]);
      setDescribeSubmitted(false);
      setVoteSubmitted(false);
      setMe((prev) => (prev ? { ...prev, secret_word: undefined, secret_word_hints: undefined } : prev));
      return;
    }
    if (ph !== "reveal") {
      prevOnlineRoundRef.current = null;
      return;
    }
    const ri = game?.round_index ?? 0;
    if (prevOnlineRoundRef.current !== null && prevOnlineRoundRef.current !== ri) {
      setDescribeDraft("");
      setDraftFeed([]);
      setVotePick([]);
      setDescribeSubmitted(false);
      setVoteSubmitted(false);
      setMe((prev) => (prev ? { ...prev, secret_word: undefined, secret_word_hints: undefined } : prev));
    }
    prevOnlineRoundRef.current = ri;
  }, [game?.phase, game?.round_index]);

  const phase = game?.phase ?? "lobby";
  const sessionRoundTotal = imposterOnlineSessionRoundTotal(game as unknown as Record<string, unknown>);
  const roundIndex = game?.round_index ?? 0;
  const roundDisplay = roundIndex + 1;
  const players = game?.players ?? {};
  const order = game?.player_order ?? [];
  const myPlayerRow = playerId ? players[playerId] : undefined;
  const descriptionLocked = describeSubmitted || Boolean(myPlayerRow?.description?.trim());
  const voteLocked = voteSubmitted || Boolean(myPlayerRow?.has_voted);

  const effectiveIsImposter = useMemo(
    () => effectiveImposterOnlineRole(me, phase, playerId, game?.imposters),
    [me, phase, playerId, game?.imposters],
  );
  const roleResolved = effectiveIsImposter === true || effectiveIsImposter === false;
  const imposterTopicLine =
    languageStartsWithAr
      ? me?.category_name_ar || me?.category_name_en || game?.category_name_ar || game?.category_name_en
      : me?.category_name_en || me?.category_name_ar || game?.category_name_en || game?.category_name_ar;

  const crewSecretWord = extractImposterOnlineSecretWord(me);
  const crewSecretWordHints = extractImposterOnlineSecretWordHints(me);

  const submitDescription = () => {
    const row = playerId ? game?.players?.[playerId] : undefined;
    if (describeSubmitted || row?.description?.trim()) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    setDescribeSubmitted(true);
    ws.send(JSON.stringify({ type: "imposter_description", text: describeDraft }));
  };

  const rosterYou = t("party.playImposterOnline.rosterYou");
  const timerHintLine =
    phaseSecondsLeft != null ? t("party.playBluff.timerSeconds", { count: phaseSecondsLeft }) : null;
  const revealLoading = phase === "reveal" && Boolean(playerId) && !roleResolved;
  const showCategoryChip =
    phase !== "lobby" &&
    phase !== "aborted_host_left" &&
    Boolean((game?.category_name_en || "").trim() || (game?.category_name_ar || "").trim());

  if (missingCode) {
    return (
      <Box className="party-shell-root" sx={{ minHeight: "100vh", bgcolor: partyPal.bg, color: partyPal.ink, px: 2.5, py: 3 }}>
        <PartyTopBar title={t("party.playImposterOnline.guestJoinTitle")} onBack={() => navigate("/")} />
        <Typography sx={{ mt: 2, color: partyPal.inkDim }}>{t("party.playImposterOnline.guestMissingCode")}</Typography>
        <Button component={RouterLink} to="/" variant="text" sx={{ color: partyPal.hot, mt: 2 }}>
          {t("party.playImposter.home")}
        </Button>
      </Box>
    );
  }

  if (!joined) {
    return (
      <Box className="party-shell-root" sx={{ minHeight: "100vh", bgcolor: partyPal.bg, color: partyPal.ink, px: 2.5, py: 3 }}>
        <PartyTopBar title={t("party.playImposterOnline.guestJoinTitle")} onBack={() => navigate("/")} />
        <Typography sx={{ mt: 1, color: partyPal.inkDim, fontSize: 15, lineHeight: 1.5 }}>
          {t("party.playImposterOnline.guestJoinHint", { code: roomCode })}
        </Typography>
        <TextField
          fullWidth
          label={t("party.playImposterOnline.displayName")}
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          onKeyDown={(e) => handlePartyRosterNameEnter(e, () => guestName.trim() && setJoined(true))}
          sx={{ mt: 2, ...fieldSx }}
        />
        <Button className="party-btn party-btn-hot" sx={{ mt: 2 }} disabled={!guestName.trim()} onClick={() => setJoined(true)}>
          {t("party.playImposterOnline.guestJoinCta")}
        </Button>
      </Box>
    );
  }

  return (
    <Box className="party-shell-root" sx={{ minHeight: "100vh", bgcolor: partyPal.bg, color: partyPal.ink, pb: 8 }}>
      <PartyTopBar title={t("party.playImposterOnline.guestRoomBar")} onBack={() => navigate("/")} />
      {wsError ? (
        <Typography sx={{ px: 2.5, color: partyPal.hot, fontSize: 14 }}>{wsError}</Typography>
      ) : null}

      {showCategoryChip ? (
        <ImposterOnlineCategoryBanner
          categoryNameEn={game?.category_name_en}
          categoryNameAr={game?.category_name_ar}
          languageStartsWithAr={languageStartsWithAr}
          partyPal={partyPal}
          topicLabel={t("party.playImposterOnline.categoryTopicLabel")}
        />
      ) : null}

      {sessionRoundTotal > 1 && phase !== "lobby" && phase !== "aborted_host_left" && phase !== "results" ? (
        <Typography sx={{ px: 2.5, pb: 0.5, fontSize: 14, fontWeight: 800, color: partyPal.hot, letterSpacing: "0.04em" }}>
          {t("party.playImposterOnline.roundBadge", { current: roundDisplay, total: sessionRoundTotal })}
        </Typography>
      ) : null}

      {phase === "lobby" ? (
        <Stack sx={{ px: 0, pt: 1 }} spacing={1.5}>
          <Typography sx={{ px: 2.5, fontWeight: 800, color: partyPal.ink }}>{t("party.playImposterOnline.guestWaitingHost")}</Typography>
          <Typography sx={{ px: 2.5, fontSize: 15, color: partyPal.inkDim, lineHeight: 1.5 }}>
            {t("party.playImposterOnline.guestWaitingBody")}
          </Typography>
          <Typography className="party-mono" sx={{ px: 2.5, fontSize: 11, letterSpacing: "0.14em", color: partyPal.inkDim }}>
            {t("party.playBluff.roomCodeLabel")}
          </Typography>
          <Typography className="party-display" sx={{ px: 2.5, fontSize: "clamp(28px, 8vw, 40px)", fontWeight: 900, letterSpacing: "0.08em", color: partyPal.ink }}>
            {roomCode}
          </Typography>
          {joinUrlGuest ? (
            <ImposterOnlineQrSharePanel
              joinUrl={joinUrlGuest}
              partyPal={partyPal}
              qrCaption={t("party.playBluff.qrCaption")}
              qrHint={t("party.playBluff.qrHint")}
              copyLabel={t("party.playBluff.copyJoinLink")}
              copyDoneLabel={t("party.playBluff.copyJoinLinkDone")}
              copied={joinLinkCopied}
              onCopy={() => void copyGuestJoinLink()}
            />
          ) : null}
          <ImposterOnlinePlayersRoster
            players={players}
            playerOrder={order}
            title={t("party.playBluff.playersTitle")}
            emptyHint={t("party.playBluff.playersEmpty")}
            partyPal={partyPal}
            myPlayerId={playerId}
            youLabel={`· ${rosterYou}`}
            hostLabel={t("party.playImposterOnline.hostBadge")}
            formatPlayerName={(pid, row) => imposterOnlinePlayerLabel(row, pid, t)}
          />
        </Stack>
      ) : null}

      {phase === "reveal" ? (
        <Stack
          direction="column"
          spacing={2}
          sx={{ px: 2.5, pt: 2, pb: 4, minHeight: "calc(100dvh - 56px)", width: "100%", alignItems: "stretch" }}
        >
          <Box sx={{ flexShrink: 0, width: "100%" }}>
            <ImposterOnlinePhaseHint
              partyPal={partyPal}
              title={t("party.playImposterOnline.phaseRevealTitle")}
              body={t("party.playImposterOnline.phaseRevealBody")}
              timerLine={timerHintLine}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minHeight: { xs: 260, sm: 300 },
            }}
          >
            {revealLoading ? (
              <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
                <CircularProgress size={36} sx={{ color: partyPal.hot }} aria-hidden />
                <Typography sx={{ color: partyPal.inkDim, textAlign: "center", fontSize: 16 }}>
                  {t("party.playImposterOnline.syncingRole")}
                </Typography>
              </Stack>
            ) : effectiveIsImposter === true ? (
              <Box className="party-anim-fade-scale-in" sx={{ width: "100%", maxWidth: 420, alignSelf: "center" }}>
                <Box
                  className="party-kan-card"
                  sx={
                    {
                      ["--card-bg" as string]: "linear-gradient(160deg, #2A0B1A, #1A0520)",
                      color: partyPal.ink,
                      py: 5,
                      px: 3,
                      textAlign: "center",
                    } as React.CSSProperties
                  }
                >
                  <PartyMascot shellId="imposter" size={110} />
                  <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.2em", color: partyPal.hot, mt: 1 }}>
                    {t("party.playImposter.imposterRoleLabel")}
                  </Typography>
                  <Typography className="party-display" sx={{ fontSize: 56, color: partyPal.hot, mt: 0.75 }}>
                    {t("party.playImposter.imposterRoleTitle")}
                  </Typography>
                  <Typography sx={{ mt: 1.5, color: partyPal.inkDim, fontSize: 14, maxWidth: "26ch", mx: "auto" }}>
                    {imposterTopicLine}
                  </Typography>
                  <Typography sx={{ mt: 1, color: partyPal.inkDim, fontSize: 13, maxWidth: "30ch", mx: "auto", lineHeight: 1.45 }}>
                    {t("party.playImposter.imposterRoleBody")}
                  </Typography>
                </Box>
                <Typography sx={{ color: partyPal.inkDim, mt: 2, textAlign: "center", fontSize: 15, lineHeight: 1.55 }}>
                  {t("party.playImposterOnline.revealWaitDescribe")}
                </Typography>
              </Box>
            ) : effectiveIsImposter === false ? (
              <Box
                className="party-anim-fade-scale-in"
                sx={{
                  width: "100%",
                  maxWidth: 420,
                  alignSelf: "center",
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: 3,
                  py: 1,
                }}
              >
                <PartyConfettiCards />
                <Box
                  className="party-kan-card"
                  sx={
                    {
                      ["--card-bg" as string]: partyPal.paper,
                      color: partyPal.paperInk,
                      py: 4,
                      px: 3,
                      textAlign: "center",
                      position: "relative",
                    } as React.CSSProperties
                  }
                >
                  <Typography
                    className="party-mono"
                    sx={{ fontSize: 11, letterSpacing: "0.2em", color: `color-mix(in oklab, ${partyPal.paperInk} 55%, transparent)` }}
                  >
                    {t("party.playImposter.crewRoleLabel")}
                  </Typography>
                  <Typography className="party-display" sx={{ fontSize: 36, mt: 1, color: partyPal.paperInk }}>
                    {t("party.playImposter.crewRoleTitle")}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1.25,
                      fontSize: 13,
                      color: `color-mix(in oklab, ${partyPal.paperInk} 72%, transparent)`,
                      lineHeight: 1.45,
                      maxWidth: "32ch",
                      mx: "auto",
                    }}
                  >
                    {t("party.playImposter.crewRoleBodyOnline")}
                  </Typography>
                  <Typography
                    className="party-mono"
                    sx={{
                      fontSize: 11,
                      letterSpacing: "0.2em",
                      color: `color-mix(in oklab, ${partyPal.paperInk} 60%, transparent)`,
                      mt: 2.5,
                    }}
                  >
                    {t("party.playImposter.secretWordLabel")}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "clamp(40px, 12vw, 64px)",
                      mt: 1,
                    }}
                  >
                    {crewSecretWord ? (
                      <Typography className="party-display" sx={{ fontSize: "clamp(36px, 10vw, 56px)", color: partyPal.paperInk }}>
                        {crewSecretWord}
                      </Typography>
                    ) : (
                      <CircularProgress size={36} sx={{ color: partyPal.hot }} aria-hidden />
                    )}
                  </Box>
                  {crewSecretWord ? (
                    <ImposterOnlineCrewHintsRow
                      hints={crewSecretWordHints}
                      hintsLabel={t("party.playImposter.hintsHeading")}
                      color={partyPal.paperInk}
                    />
                  ) : null}
                </Box>
                <Typography sx={{ color: partyPal.inkDim, mt: 2, textAlign: "center", fontSize: 15, lineHeight: 1.55 }}>
                  {t("party.playImposterOnline.revealWaitDescribe")}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Stack>
      ) : null}

      {phase === "describe" ? (
        <Stack sx={{ px: 2.5, pt: 2 }} spacing={2}>
          <ImposterOnlinePhaseHint
            partyPal={partyPal}
            title={t("party.playImposterOnline.phaseDescribeTitle")}
            body={
              effectiveIsImposter === true
                ? t("party.playImposterOnline.phaseDescribeBodyImposter")
                : t("party.playImposterOnline.phaseDescribeBodyCrew")
            }
            timerLine={timerHintLine}
          />
          {effectiveIsImposter === true ? (
            <Stack spacing={2}>
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: partyPal.ink, mb: 1 }}>
                  {t("party.playImposterOnline.draftFeedTitle")}
                </Typography>
                {draftFeed.map((d, i) => (
                  <Typography key={i} sx={{ fontSize: 15, color: partyPal.ink, mb: 0.5, lineHeight: 1.45 }}>
                    {imposterOnlineAvatarEmoji(d.from)} {imposterOnlinePlayerLabel(players[d.from], d.from, t)}: {d.text}
                  </Typography>
                ))}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: partyPal.ink, mb: 1 }}>
                  {t("party.playImposterOnline.describeLiveSubmissionsTitle")}
                </Typography>
                {order.map((pid) => (
                  <Paper
                    key={pid}
                    sx={{
                      p: 2,
                      mb: 1,
                      borderRadius: 2,
                      bgcolor: alpha(partyPal.ink, 0.05),
                      border: `1px solid ${partyPal.inkFaint}`,
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: 16, color: partyPal.ink }}>
                      {imposterOnlineAvatarEmoji(pid)} {imposterOnlinePlayerLabel(players[pid], pid, t)}
                    </Typography>
                    <Typography sx={{ mt: 1, color: partyPal.ink, fontSize: 16, lineHeight: 1.5 }}>
                      {(players[pid]?.description ?? "").trim() || "—"}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Stack>
          ) : null}
          <TextField
            multiline
            minRows={3}
            value={describeDraft}
            onChange={(e) => setDescribeDraft(e.target.value)}
            placeholder={t("party.playImposterOnline.describePlaceholder")}
            fullWidth
            disabled={descriptionLocked}
            sx={fieldSx}
          />
          <Button
            className="party-btn party-btn-hot"
            disabled={descriptionLocked || !describeDraft.trim()}
            onClick={submitDescription}
          >
            {descriptionLocked ? t("party.playImposterOnline.submitted") : t("party.playImposterOnline.submitDescription")}
          </Button>
        </Stack>
      ) : null}

      {phase === "reveal_answers" ? (
        <Stack sx={{ px: 2.5, pt: 2 }} spacing={2}>
          <ImposterOnlinePhaseHint
            partyPal={partyPal}
            title={t("party.playImposterOnline.phaseRevealAnswersTitle")}
            body={t("party.playImposterOnline.phaseRevealAnswersBody")}
          />
          <Typography sx={{ fontWeight: 800, fontSize: 17, color: partyPal.ink }}>
            {t("party.playImposterOnline.allDescriptions")}
          </Typography>
          {order.map((pid) => (
            <Paper key={pid} sx={{ p: 2, borderRadius: 2, bgcolor: alpha(partyPal.ink, 0.05), border: `1px solid ${partyPal.inkFaint}` }}>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: partyPal.ink }}>
                {imposterOnlineAvatarEmoji(pid)} {imposterOnlinePlayerLabel(players[pid], pid, t)}
              </Typography>
              <Typography sx={{ mt: 1, color: partyPal.ink, fontSize: 16, lineHeight: 1.5 }}>{players[pid]?.description || "—"}</Typography>
            </Paper>
          ))}
        </Stack>
      ) : null}

      {phase === "vote" ? (
        <Stack sx={{ px: 2.5, pt: 2 }} spacing={2}>
          <ImposterOnlinePhaseHint
            partyPal={partyPal}
            title={t("party.playImposterOnline.phaseVoteTitle")}
            body={t("party.playImposterOnline.phaseVoteBody", { count: imposterCount })}
            timerLine={timerHintLine}
          />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: partyPal.ink, mb: 1 }}>
              {t("party.playImposterOnline.voteProgressTitle")}
            </Typography>
            <Stack spacing={0.75}>
              {order.map((pid) => {
                const voted = Boolean(players[pid]?.has_voted);
                return (
                  <Typography key={pid} sx={{ fontSize: 14, color: partyPal.inkDim }}>
                    {imposterOnlineAvatarEmoji(pid)} {imposterOnlinePlayerLabel(players[pid], pid, t)} —{" "}
                    {voted ? t("party.playImposterOnline.voteProgressVoted") : t("party.playImposterOnline.voteProgressPending")}
                  </Typography>
                );
              })}
            </Stack>
          </Box>
          <Stack spacing={1.5}>
            {order
              .filter((pid) => pid !== playerId)
              .map((pid) => {
                const on = votePick.includes(pid);
                const desc = (players[pid]?.description ?? "").trim() || "—";
                return (
                  <Paper
                    key={pid}
                    role="button"
                    tabIndex={voteLocked ? -1 : 0}
                    onClick={() => {
                      if (voteLocked) return;
                      setVotePick((prev) => {
                        if (prev.includes(pid)) return prev.filter((x) => x !== pid);
                        if (prev.length >= imposterCount) return prev;
                        return [...prev, pid];
                      });
                    }}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      cursor: voteLocked ? "default" : "pointer",
                      opacity: voteLocked ? 0.55 : 1,
                      border: `2px solid ${on ? partyPal.hot : partyPal.inkFaint}`,
                      bgcolor: on ? alpha(partyPal.hot, 0.08) : alpha(partyPal.ink, 0.04),
                      outline: "none",
                      "&:focus-visible": { boxShadow: `0 0 0 2px ${partyPal.hot}` },
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: 16, color: partyPal.ink }}>
                      {imposterOnlineAvatarEmoji(pid)} {imposterOnlinePlayerLabel(players[pid], pid, t)}
                    </Typography>
                    <Typography sx={{ mt: 1, color: partyPal.ink, fontSize: 15, lineHeight: 1.5 }}>{desc}</Typography>
                  </Paper>
                );
              })}
          </Stack>
          {voteLocked ? (
            <Typography sx={{ fontSize: 15, color: partyPal.inkDim, fontWeight: 600 }}>
              {t("party.playImposterOnline.voteSubmitted")}
            </Typography>
          ) : null}
        </Stack>
      ) : null}

      {phase === "between_rounds" ? (
        <Stack sx={{ px: 2.5, pt: 2 }} spacing={2.5} alignItems="stretch">
          <ImposterOnlinePhaseHint
            partyPal={partyPal}
            title={t("party.playImposterOnline.betweenRoundsTitle")}
            body={t("party.playImposterOnline.betweenRoundsBody")}
          />
          {String(game?.last_completed_round_word ?? "").trim() ? (
            <Typography className="party-display" sx={{ textAlign: "center", fontSize: "clamp(28px, 8vw, 44px)", color: partyPal.ink, fontWeight: 800 }}>
              {String(game?.last_completed_round_word ?? "").trim()}
            </Typography>
          ) : null}
          <Typography sx={{ textAlign: "center", color: partyPal.inkDim, fontSize: 15, lineHeight: 1.55 }}>
            {t("party.playImposterOnline.betweenRoundsWaitHost")}
          </Typography>
        </Stack>
      ) : null}

      {phase === "results" && game?.results ? (
        <Stack sx={{ px: 2.5, pt: 2 }} spacing={2} alignItems="stretch">
          <Box
            className="party-kan-card party-anim-fade-scale-in"
            sx={
              {
                ["--card-bg" as string]: partyPal.paper,
                color: partyPal.paperInk,
                py: 3,
                px: 2.5,
                textAlign: "center",
              } as React.CSSProperties
            }
          >
            <Typography
              className="party-mono"
              sx={{
                fontSize: 12,
                letterSpacing: "0.14em",
                color: `color-mix(in oklab, ${partyPal.paperInk} 65%, transparent)`,
              }}
            >
              {t("party.playImposterOnline.resultsOutcomeLabel")}
            </Typography>
            <Typography
              className="party-display"
              sx={{
                fontSize: "clamp(26px, 7vw, 36px)",
                mt: 1.25,
                lineHeight: 1.15,
                color: partyPal.paperInk,
              }}
            >
              {game.results.crew_wins ? t("party.playImposter.outcomeCrewWin") : t("party.playImposter.outcomeImposterWin")}
            </Typography>
          </Box>
          <Box
            className="party-kan-card party-anim-fade-scale-in"
            sx={
              {
                ["--card-bg" as string]: `linear-gradient(155deg, color-mix(in oklab, ${partyPal.hot} 22%, ${partyPal.bg2}), ${partyPal.bg2})`,
                color: partyPal.ink,
                py: 3.5,
                px: 2.5,
                textAlign: "center",
              } as React.CSSProperties
            }
          >
            <Typography className="party-mono" sx={{ fontSize: 12, letterSpacing: "0.14em", color: partyPal.hot }}>
              {Array.isArray(game.results.session_words) && game.results.session_words.length > 1
                ? t("party.playImposterOnline.resultsSessionWordsLabel")
                : t("party.playImposterOnline.resultsSecretWordLabel")}
            </Typography>
            {Array.isArray(game.results.session_words) && game.results.session_words.filter(Boolean).length > 1 ? (
              <Stack spacing={1.25} sx={{ mt: 1.5, alignItems: "center" }}>
                {game.results.session_words.filter(Boolean).map((w, i) => (
                  <Typography key={`${i}-${w}`} className="party-display" sx={{ fontSize: "clamp(22px, 6vw, 34px)", color: partyPal.ink, lineHeight: 1.2 }}>
                    {i + 1}. {w}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography className="party-display" sx={{ fontSize: "clamp(30px, 8vw, 44px)", mt: 1.5, color: partyPal.ink }}>
                {game.results.secret_word ?? game.results.session_words?.[0] ?? "—"}
              </Typography>
            )}
            {(() => {
              const rh = game.results.secret_word_hints;
              const hints = Array.isArray(rh)
                ? rh.filter((x): x is string => typeof x === "string").map((s) => s.trim()).filter(Boolean)
                : [];
              if (hints.length === 0) return null;
              return (
                <ImposterOnlineCrewHintsRow
                  hints={hints}
                  hintsLabel={t("party.playImposter.hintsHeading")}
                  color={partyPal.ink}
                  labelColor={partyPal.inkDim}
                />
              );
            })()}
            <Typography sx={{ fontSize: 16, color: partyPal.inkDim, mt: 2, lineHeight: 1.5, maxWidth: "36ch", mx: "auto" }}>
              {Array.isArray(game.results.session_words) && game.results.session_words.filter(Boolean).length > 1
                ? t("party.playImposterOnline.resultsSessionWordsCaption")
                : t("party.playImposterOnline.resultsSecretWordCaption")}
            </Typography>
          </Box>
          <Button className="party-btn party-btn-hot" fullWidth component={RouterLink} to="/">
            {t("party.playImposter.home")}
          </Button>
        </Stack>
      ) : null}

      {phase === "aborted_host_left" ? (
        <Stack sx={{ px: 2.5, pt: 3 }} spacing={1.5}>
          <Typography sx={{ color: partyPal.hot, fontWeight: 800, fontSize: 18 }}>{t("party.playImposterOnline.hostLeft")}</Typography>
          <Typography sx={{ color: partyPal.inkDim, fontSize: 16, lineHeight: 1.55 }}>{t("party.playImposterOnline.hostLeftBody")}</Typography>
          <Button component={RouterLink} to="/" variant="text" sx={{ color: partyPal.hot }}>
            {t("party.playImposter.home")}
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
}
